// Composer Studio — 작성 캔버스 위젯 경계 드래그 오버레이(폭 비율 span 조정).
//
// 같은 줄에 나란히 놓인 **인접 두 위젯 사이 경계**(칸과 칸 사이 gap)에 얇은 hit strip을 띄운다.
// strip을 pointer 드래그하면 두 위젯의 span(0~1 비율)이 **합을 유지한 채** 좌우로 나뉜다 —
// 좌측이 넓어지면 우측이 그만큼 좁아지고, 같은 행의 다른 위젯과 줄 끝 빈 트랙(filler)은 불변이다.
// 시각 핸들(■)은 그리지 않는다 — 커서(ew-resize)로만 알리는 표 셀 경계(TableEdgeResizeOverlay)·
// 노드 경계(NodeEdgeResizeOverlay)와 같은 "테두리를 잡아 끈다" 감각.
//
// (A) 계산은 순수 모듈(spanResize)로 — 여기서는 DOM 측정과 드래그 UX만 담당한다.
//   드래그 중에는 onPreview(히스토리 미기록), pointerup에 onCommit(undo 1스텝) — 노드 경계와 동일.
//   ⚠️ 경계는 **두 위젯을 동시에** 바꾸므로 콜백이 편집 배열을 받는다(호스트가 한 트랜잭션으로 적용).
//
// (B) 미지정(상속) 위젯은 드래그 시작 시 실측 폭 ÷ 행 트랙 합으로 구체 비율 전환(spanResize 정책).
//   → 화면에 보이던 그 비율에서 출발한다(WYSIWYG). 노드의 fill/hug→실측 px 전환과 동형.
//
// (C) 좌표/단위 — 캔버스는 줌(transform scale) 래퍼 안이라 **미스케일(레이아웃) px**로 통일한다:
//   위치·폭은 rectRelativeToUnscaled, 행 트랙 합은 getComputedStyle(gridTemplateColumns),
//   포인터 델타는 ÷ canvasScale. 셋이 같은 단위라 줌이 몇 %든 드래그가 화면과 일치한다.
//
// (D) 이동 그립(⠿, 칸 좌상단 z-10)과의 분리 — strip은 행 상단 GRIP_RESERVE px를 비운다.
//   비우지 않으면 오른쪽 위젯의 그립을 덮어 드래그 이동이 막힌다(안쪽=이동, 사이=리사이즈).
//
// 엔진 DOM은 무수정. [data-composer-widget-id] 요소에서 grid 칸을 거슬러 올라가 행을 재구성하므로
// 비율 배치(행 컨테이너 data-composer-row)든 기존 클래스 grid(다단 섹션)든 같은 코드로 잡힌다.
// 레이아웃 중립: absolute 오버레이만, 흐름에 요소를 남기지 않는다.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ComposerWidget } from '@safetyhub/shared';
import { resolveWidgetSpanRatio } from '@safetyhub/report-composer';
import { canvasScale, rectRelativeToUnscaled } from './canvasRect';
import { resizeSpanPair } from './spanResize';

/** 경계 hit strip 폭(px) — 칸 사이 gap(기본 16px) 안에 들어가 위젯 본문 클릭을 가리지 않는다. */
const STRIP = 9;

/** 행 상단에서 strip을 비우는 높이(px) — 칸 좌상단 이동 그립(⠿)을 덮지 않기 위해. */
const GRIP_RESERVE = 18;

/** strip을 만들 최소 높이(px) — 그립을 비우고 남는 게 이보다 얇으면 행 전체 높이를 쓴다. */
const MIN_STRIP_HEIGHT = 10;

/** 같은 행 판정 허용 오차(px) — grid 칸은 top이 같지만 서브픽셀 반올림을 흡수한다. */
const ROW_TOP_TOLERANCE = 4;

/** grid 칸을 찾아 거슬러 올라갈 최대 깊이(무한 루프 방지). */
const MAX_ITEM_WALK = 12;

/** 경계 드래그 결과 — 위젯 하나에 기록할 폭 비율. */
export interface SpanEdit {
  id: string;
  ratio: number;
}

/** 화면에서 잡을 수 있는 경계 하나(좌우 위젯 쌍 + 캔버스 상대 위치, 전부 미스케일 px). */
interface SpanBoundary {
  leftId: string;
  rightId: string;
  /** strip 중심 x(두 칸 사이 gap의 중앙). */
  centerX: number;
  top: number;
  height: number;
  leftWidthPx: number;
  rightWidthPx: number;
  /** 비율 1.0에 해당하는 행 폭 = 그 행 grid 트랙 폭의 합(gap 제외). */
  rowRatioWidthPx: number;
}

/** grid 칸(=display:grid 조상의 직속 자식) 하나 — 측정 결과. */
interface GridCell {
  id: string;
  grid: HTMLElement;
  top: number;
  left: number;
  width: number;
  height: number;
}

/**
 * 위젯 DOM에서 그 위젯이 놓인 grid 칸과 grid 컨테이너를 찾는다. grid 안이 아니면(1단 세로 흐름)
 * null — 그런 위젯은 옆에 이웃이 없어 경계도 없다.
 * 칸은 위젯 요소 자신이 아니라 **grid 직속 자식**이다(admin은 dnd 래퍼 ComposerCanvasWidget를 끼운다).
 */
function findGridCell(canvas: HTMLElement, el: HTMLElement): { grid: HTMLElement; cell: HTMLElement } | null {
  let cell: HTMLElement = el;
  for (let i = 0; i < MAX_ITEM_WALK; i += 1) {
    const parent = cell.parentElement;
    if (!parent || parent === canvas) return null;
    // 다른 위젯 안에 들어 있으면(1단 섹션·반복·분기 등 컨테이너 위젯 속) 그 위젯이 칸의 주인이다 —
    // 여기서 멈추지 않으면 바깥 grid의 칸을 이 위젯의 칸으로 오인해 엉뚱한 경계가 생긴다.
    if (parent.hasAttribute('data-composer-widget-id')) return null;
    if (window.getComputedStyle(parent).display === 'grid') return { grid: parent, cell };
    cell = parent;
  }
  return null;
}

/**
 * grid의 사용 트랙 폭 합(px) — getComputedStyle의 grid-template-columns는 계산된 **used value**
 * (예: `"300px 150px"`)라 fr·filler 트랙도 실제 px로 읽힌다. 파싱 실패 시 null(호출부가 폴백).
 */
function gridTrackSumPx(grid: HTMLElement): number | null {
  const raw = window.getComputedStyle(grid).gridTemplateColumns;
  if (!raw || raw === 'none') return null;
  let sum = 0;
  for (const part of raw.split(/\s+/)) {
    const v = Number.parseFloat(part);
    if (!Number.isFinite(v)) return null;
    sum += v;
  }
  return sum > 0 ? sum : null;
}

/**
 * 폭 비율이 **실제로 통하는 자리**만 골라내기 위한 부모 색인 — id → 그 위젯을 담은 group(섹션) id.
 * span은 다단 섹션의 행 분배 축이라 그룹 자식에서만 의미가 있다. 반복/모달/분기 슬롯 children과
 * 루트 직속은 수록하지 않아, 그런 컨테이너가 우연히 grid로 렌더돼도 경계가 생기지 않는다.
 */
function collectSectionParents(tree: ComposerWidget[]): Map<string, string> {
  const out = new Map<string, string>();
  const walk = (ws: ComposerWidget[], groupId: string | null): void => {
    for (const w of ws) {
      if (groupId) out.set(w.id, groupId);
      if (w.kind === 'group') walk(w.children, w.id);
      else if (w.kind === 'modalTrigger' || w.kind === 'repeat') walk(w.children, null);
      else if (w.kind === 'branch') for (const slot of w.branches) walk(slot.children, null);
    }
  };
  walk(tree, null);
  return out;
}

/**
 * 경계 위치를 좌우하는 축만 접은 배치 시그니처 — 재측정 트리거용(자매 오버레이의 remeasureKey와
 * 같은 결의 "저렴한 시그니처").
 *
 * 트리 **참조**를 effect deps에 두면 라벨 한 글자를 칠 때마다 전 위젯 getComputedStyle walk가
 * 다시 돈다(collectBoundaries는 위젯마다 조상을 거슬러 오르며 display를 읽고 grid마다 트랙을
 * 파싱한다 — 편집 중 매 키 입력에 물리기엔 비싸다). 경계가 실제로 움직이는 건 구성·순서·폭
 * 축이 바뀔 때뿐이라 그 축만 문자열로 접는다.
 *
 * 세로 축(행 높이)은 여기 없다 — 라벨 줄바꿈 등으로도 변하지만 그건 캔버스 ResizeObserver가 잡는다.
 */
function layoutSignature(tree: ComposerWidget[], view: string): string {
  const parts: string[] = [];
  const walk = (ws: ComposerWidget[]): void => {
    for (const w of ws) {
      // 순서는 walk 순서로 표현된다(별도 토큰 불필요). order는 뷰별 재정렬 축이라 따로 싣는다.
      const r = w.responsive?.[view];
      parts.push(`${w.id}|${w.span ?? ''}|${r?.span ?? ''}|${r?.hidden ? 1 : ''}|${r?.order ?? ''}`);
      if (w.kind === 'group') {
        // 단 수(뷰 오버라이드 포함)와 탭 변형은 행 트랙 자체를 바꾼다(tabs → 본문 1단 고정).
        parts.push(`g${w.columns ?? ''}|${r?.columns ?? ''}|${w.displayVariant ?? ''}`);
        walk(w.children);
      } else if (w.kind === 'modalTrigger' || w.kind === 'repeat') walk(w.children);
      else if (w.kind === 'branch') for (const slot of w.branches) walk(slot.children);
    }
  };
  walk(tree);
  return parts.join(';');
}

/** 캔버스 전체에서 드래그 가능한 위젯 경계를 수집한다(DOM 측정 — 렌더 중이 아니라 effect에서 호출). */
function collectBoundaries(canvas: HTMLElement, tree: ComposerWidget[]): SpanBoundary[] {
  const sectionOf = collectSectionParents(tree);
  const cells: GridCell[] = [];
  for (const el of Array.from(canvas.querySelectorAll<HTMLElement>('[data-composer-widget-id]'))) {
    const id = el.dataset.composerWidgetId;
    if (!id || !sectionOf.has(id)) continue;
    const found = findGridCell(canvas, el);
    if (!found) continue;
    const rect = rectRelativeToUnscaled(canvas, found.cell);
    if (rect.width <= 0 || rect.height <= 0) continue;
    cells.push({ id, grid: found.grid, ...rect });
  }

  // grid 컨테이너별 → 행별로 묶는다. 비율 배치는 행마다 컨테이너가 따로고(data-composer-row),
  // 기존 클래스 grid는 한 컨테이너에 여러 행이 흐르므로 top으로 행을 가른다.
  const byGrid = new Map<HTMLElement, GridCell[]>();
  for (const c of cells) {
    const list = byGrid.get(c.grid);
    if (list) list.push(c);
    else byGrid.set(c.grid, [c]);
  }

  const out: SpanBoundary[] = [];
  for (const [grid, list] of byGrid) {
    const trackSum = gridTrackSumPx(grid);
    // 행 우선 정렬 — top을 허용오차 단위로 버킷화해 비교(부동소수 top의 비교 일관성 확보) 후 left.
    list.sort((a, b) => {
      const ra = Math.round(a.top / ROW_TOP_TOLERANCE);
      const rb = Math.round(b.top / ROW_TOP_TOLERANCE);
      return ra !== rb ? ra - rb : a.left - b.left;
    });
    let row: GridCell[] = [];
    const flush = (): void => {
      if (row.length >= 2) {
        // 트랙 합을 못 읽으면 그 행 칸 폭 합으로 폴백(합<1인 행에선 filler를 모르니 근사가 된다).
        const rowWidth = trackSum ?? row.reduce((acc, c) => acc + c.width, 0);
        const rowTop = Math.min(...row.map((c) => c.top));
        const rowBottom = Math.max(...row.map((c) => c.top + c.height));
        const full = rowBottom - rowTop;
        const usable = full - GRIP_RESERVE;
        const fits = usable >= MIN_STRIP_HEIGHT;
        for (let i = 0; i + 1 < row.length; i += 1) {
          const a = row[i];
          const b = row[i + 1];
          // 두 칸이 같은 섹션 형제일 때만 — 폭 비율은 그 축에서만 통한다(반복/분기 등 오탐 차단).
          const parent = sectionOf.get(a.id);
          if (!parent || parent !== sectionOf.get(b.id)) continue;
          out.push({
            leftId: a.id,
            rightId: b.id,
            centerX: (a.left + a.width + b.left) / 2,
            top: fits ? rowTop + GRIP_RESERVE : rowTop,
            height: fits ? usable : full,
            leftWidthPx: a.width,
            rightWidthPx: b.width,
            rowRatioWidthPx: rowWidth,
          });
        }
      }
      row = [];
    };
    for (const c of list) {
      if (row.length > 0 && Math.abs(c.top - row[0].top) > ROW_TOP_TOLERANCE) flush();
      row.push(c);
    }
    flush();
  }
  return out;
}

/**
 * 작성 캔버스 위젯 경계 리사이즈 오버레이 — 캔버스 div(position:relative) 내부에 absolute로 둔다.
 * 인접 위젯 쌍마다 경계 strip 1개. 드래그 = 두 위젯의 폭 비율을 합 보존으로 재분배.
 */
export function WidgetEdgeResizeOverlay({
  canvas,
  tree,
  view,
  widgetById,
  onPreview,
  onCommit,
  remeasureKey,
}: {
  canvas: HTMLElement | null;
  /** 활성 트리 — 변경 시 재측정 트리거(위젯 추가/삭제/비율 편집으로 칸 위치가 바뀐다). */
  tree: ComposerWidget[];
  /** 활성 뷰 키 — 현재 비율 해소(responsive[view].span ?? span)에 쓴다. 기록은 호스트가 담당. */
  view: string;
  /** id로 위젯 조회(트리 재귀) — 드래그 시작 시점의 지정 비율을 읽는다. */
  widgetById: (id: string) => ComposerWidget | null;
  /** 드래그 중 연속 미리보기(히스토리 미기록) — 두 위젯을 한 번에 적용할 것. */
  onPreview: (edits: SpanEdit[]) => void;
  /** pointerup 시 1개 히스토리 엔트리 커밋 — 두 위젯을 한 번에 적용할 것. */
  onCommit: (edits: SpanEdit[]) => void;
  /** 추가 재측정 트리거(활성 슬롯 전환 등). 값이 바뀌면 재측정한다. */
  remeasureKey?: unknown;
}) {
  const [boundaries, setBoundaries] = useState<SpanBoundary[]>([]);
  const [tick, setTick] = useState(0);
  // 드래그 중 재측정 억제 — 잡고 있는 경계가 포인터를 따라 움직이면 타깃이 흔들린다(+측정 비용).
  const draggingRef = useRef(false);
  // 드래그 중 등록한 window 리스너 해제기(도중 언마운트 시 누수 방지 — 노드 경계와 동일 규율).
  const dragCleanupRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    return () => {
      dragCleanupRef.current?.();
      dragCleanupRef.current = null;
    };
  }, []);

  // 측정 effect가 읽을 최신 트리 — deps엔 트리 참조 대신 배치 시그니처를 두므로 값은 ref로 넘긴다.
  // 이 effect를 측정 effect보다 **먼저** 선언해, 같은 커밋에서 항상 먼저 갱신되게 한다.
  const treeRef = useRef(tree);
  useEffect(() => {
    treeRef.current = tree;
  });

  // 배치 시그니처 — 트리가 새 참조여도 경계를 움직이는 축이 그대로면 재측정하지 않는다.
  const layoutKey = useMemo(() => layoutSignature(tree, view), [tree, view]);

  // rect는 DOM 측정이라 렌더 후 확정 — 배치/뷰/재측정 키 변화와 캔버스 리사이즈마다 다시 잰다.
  useEffect(() => {
    if (!canvas) {
      setBoundaries([]);
      return;
    }
    if (!draggingRef.current) setBoundaries(collectBoundaries(canvas, treeRef.current));
  }, [canvas, layoutKey, view, remeasureKey, tick]);

  useEffect(() => {
    if (!canvas) return;
    const bump = (): void => setTick((n) => n + 1);
    const ro = new ResizeObserver(bump);
    ro.observe(canvas);
    window.addEventListener('resize', bump);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', bump);
    };
  }, [canvas]);

  const beginResize = useCallback(
    (b: SpanBoundary, e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!canvas) return;
      const leftWidget = widgetById(b.leftId);
      const rightWidget = widgetById(b.rightId);
      if (!leftWidget || !rightWidget) return;
      const start = {
        pointerX: e.clientX,
        leftRatio: resolveWidgetSpanRatio(leftWidget, view),
        rightRatio: resolveWidgetSpanRatio(rightWidget, view),
        // 줌 배율은 드래그 시작 시점 고정 — 드래그 중 줌이 바뀌는 조작은 없다.
        scale: canvasScale(canvas) || 1,
      };
      draggingRef.current = true;

      const editsFor = (ev: PointerEvent): SpanEdit[] => {
        const next = resizeSpanPair({
          leftRatio: start.leftRatio,
          rightRatio: start.rightRatio,
          leftMeasuredPx: b.leftWidthPx,
          rightMeasuredPx: b.rightWidthPx,
          rowRatioWidthPx: b.rowRatioWidthPx,
          deltaPx: (ev.clientX - start.pointerX) / start.scale,
        });
        return [
          { id: b.leftId, ratio: next.left },
          { id: b.rightId, ratio: next.right },
        ];
      };

      const onMove = (ev: PointerEvent): void => {
        if (!draggingRef.current) return;
        onPreview(editsFor(ev));
      };

      const removeListeners = (): void => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        dragCleanupRef.current = null;
      };

      const onUp = (ev: PointerEvent): void => {
        if (!draggingRef.current) {
          removeListeners();
          return;
        }
        // ⚠️ edits 계산은 드래그 플래그를 내리기 **전에**(계산이 죽으면 히스토리 미기록=undo 불능).
        const edits = editsFor(ev);
        draggingRef.current = false;
        removeListeners();
        onCommit(edits);
        // 새 폭으로 경계 위치를 다시 잡는다(커밋 렌더 뒤 effect에서 측정).
        setTick((n) => n + 1);
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      dragCleanupRef.current = removeListeners;
    },
    [canvas, view, widgetById, onPreview, onCommit],
  );

  if (!canvas || boundaries.length === 0) return null;

  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      {boundaries.map((b, i) => (
        <div
          key={`wspan-${b.leftId}-${b.rightId}-${i}`}
          role="separator"
          aria-orientation="vertical"
          aria-label="폭 비율 조절"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => beginResize(b, e)}
          style={{
            position: 'absolute',
            top: b.top,
            left: b.centerX - STRIP / 2,
            width: STRIP,
            height: b.height,
            // 배경 없음(투명) — 시각 핸들 미표시, 커서로만 조절 가능 영역을 알린다.
            cursor: 'ew-resize',
            pointerEvents: 'auto',
            touchAction: 'none',
            // 이동 그립(z-10)보다 위, 분기 슬롯 탭(z-26)보다 아래.
            zIndex: 20,
          }}
        />
      ))}
    </div>
  );
}
